'use client';

import { useTranslations } from 'next-intl';
import clsx from 'clsx';
import { ProductEngraveContainer } from '@headless-workspace/domain/common/client';
import { ProductInfoTabParamsValue } from '@headless-workspace/domain/common/server';
import { GlowTextBody, PropsWithFontFamily } from '@headless-workspace/glow-ds';
import { Component, ImageWithSrc } from '@headless-workspace/typings';
import { ProductInfoCTA } from './ProductInfoCTA';
import { ProductInfoDescription } from './ProductInfoDescription';

type ProductInfoSectionProps = PropsWithFontFamily & {
    productInfoTabParams: ProductInfoTabParamsValue[];
    isEngravable: boolean;
    productImage?: ImageWithSrc;
};

export const DESCRIPTION_TAB_ID = 'description';

export const ProductInfoSection: Component<ProductInfoSectionProps> = ({
    productInfoTabParams,
    fontFamily,
    isEngravable,
    productImage,
}) => {
    const t = useTranslations();

    const renderTabContent = (productInfoTabParam: ProductInfoTabParamsValue) => {
        let labelElement = (
            <GlowTextBody
                fontFamily={fontFamily}
                text={t(productInfoTabParam.label)}
                fontWeight={'bold'}
                TagName={'span'}
            />
        );

        if (productInfoTabParam.id === DESCRIPTION_TAB_ID && productInfoTabParam.displayContentInProductPage) {
            labelElement = (
                <GlowTextBody
                    text={t('Discover.Pdp.productInfo.productTab.action.readMore')}
                    textDecoration={'underline'}
                    fontFamily={fontFamily}
                    TagName={'span'}
                />
            );

            return (
                productInfoTabParam.value && (
                    <div className={'flex flex-col gap-0.75 pb-1'}>
                        <ProductInfoDescription
                            value={productInfoTabParam.value}
                            label={t(productInfoTabParam.label)}
                            fontFamily={fontFamily}
                        />
                        <ProductInfoCTA
                            productTabs={productInfoTabParams}
                            openTabWithId={productInfoTabParam.id}
                            labelElement={labelElement}
                            isHyperText={true}
                            fontFamily={fontFamily}
                        />
                    </div>
                )
            );
        }

        return (
            <div className={'border-t-border-primary border-t-medium border-solid'}>
                <ProductInfoCTA
                    productTabs={productInfoTabParams}
                    openTabWithId={productInfoTabParam.id}
                    labelElement={labelElement}
                    isHyperText={false}
                    fontFamily={fontFamily}
                />
            </div>
        );
    };

    return (
        <section className={clsx(productInfoTabParams && isEngravable && 'flex flex-col gap-0.5')}>
            {productInfoTabParams && (
                <ul className={'scrollbar-hide overflow-x-scroll max-w-full whitespace-nowrap'}>
                    {productInfoTabParams.map((productTabParam) => (
                        <li id={productTabParam.id} key={productTabParam.id} className={productTabParam.className}>
                            {renderTabContent(productTabParam)}
                        </li>
                    ))}
                </ul>
            )}
            {isEngravable && <ProductEngraveContainer fontFamily={fontFamily} productImage={productImage} />}
        </section>
    );
};
